<template>
  <Sheet>
    <template #leading>
      <Sheet.Button :icon="CloseIcon" @click="close()" />
    </template>
    <template #title>Select copyright owner</template>

    <Sheet.Body class="px-0!">
      <template v-for="owner in owners" :key="owner.id">
        <button
          type="button"
          class="flex h-[54px] w-full items-center gap-4 px-6 text-left"
          @click="select(owner)"
        >
          <Avatar :src="owner.avatarUrl" :size="28" class="shrink-0" />
          <div class="min-w-0 flex-1">
            <p
              class="truncate font-secondary text-[16px] leading-[18px] text-gray-800"
            >
              {{ owner.name }}
            </p>
            <p
              v-if="isOrganization(owner)"
              class="truncate font-secondary text-[13px] leading-[17px] text-gray-500"
            >
              Managed by {{ managedBy(owner) }}
            </p>
          </div>
          <OrganizationIcon
            v-if="isOrganization(owner)"
            class="size-5 shrink-0 text-gray-800/50"
          />
          <div
            v-if="owner.id === selectedId"
            class="size-5 bg-green-900 rounded-full flex items-center justify-center"
          >
            <CheckmarkIcon class="size-3 text-white" />
          </div>
        </button>
        <div class="ml-[68px] mr-5 h-px bg-gray-800/10" />
      </template>

      <button
        type="button"
        class="flex h-[54px] w-full items-center gap-4 px-6 text-left"
        @click="addNew"
      >
        <div
          class="flex size-7 shrink-0 items-center justify-center rounded-full bg-green-700/10"
        >
          <AddIcon class="size-4 text-green-700" />
        </div>
        <span class="font-medium text-[16px] leading-[18px] text-green-700">
          Add new copyright owner
        </span>
      </button>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Sheet, Avatar, useSheet } from '@dacr/library'
import {
  OrganizationIcon,
  CheckmarkIcon,
  AddIcon,
  CloseIcon,
} from '@dacr/icons'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useUploadStore } from '@/features/upload/store'
import {
  CopyrightOwnerType,
  useCopyrightOwnersNewQuery,
  type CopyrightOwnersNewQuery,
} from '@/shared/graphql/legacy/generated'

type OwnerItem = CopyrightOwnersNewQuery['copyrightOwnersNew']['items'][number]

const { close } = useSheet()
const uploadStore = useUploadStore()

const { result } = useCopyrightOwnersNewQuery()

const owners = computed(() => result.value?.copyrightOwnersNew.items ?? [])
const selectedId = computed(() => uploadStore.draft?.copyrightOwnerId)

const isOrganization = (owner: OwnerItem) =>
  owner.type === CopyrightOwnerType.Organization

const managedBy = (owner: OwnerItem) =>
  owner.owner && 'name' in owner.owner ? owner.owner.name : ''

const select = (owner: OwnerItem) => {
  uploadStore.updateDraft({ copyrightOwnerId: owner.id })
  close()
}

const addNew = () => openDialog(Dialog.NewCopyrightOwner, { stack: 'cover' })
</script>
